import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProductComments from '../../../components/productInfo/comments/ProductComments';
import ProductPricing from '../../../components/productInfo/pricing/PricingInfo';
import { Product } from '../../../models/product/types';
import { ProductReferences, TeamMember, GetUploadedDocumentsAndImagesCountResponse, SustainabilityDocument, LeedDocumentType } from '../../../redux/actions/productActions/types';
import { MFRPProductItemContainer } from './MFRProduct.styles';
import MFRProductInfo from './mfrProductInfo/MFRProductInfo';
import { MFRProject } from '../../../models/project/types';
import ProductItemHeader from '../../../components/favoritProducts/favoriteProductItem/productItemHeader/ProductItemHeader';
import ProductUploads, { UploadType } from '../../../components/productInfo/uploads/UploadsInfo';
import { isFunctionalityEnabled } from '../../../../config/functionalityAvailability';
import { UPLOAD_PHOTOS } from '../../../../config/paramsConfig';
import { SelectData } from '../../../models/common/types';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { getAllSustainabilityDocuments } from '../../../helpers/transparencyCatalogHelper';
import { getLeedDocumentTypesAction, getLeedLEMDocumentTypesAction } from '../../../redux/actions/productActions';
import { ActionResponse } from '../../../models/actionResponse/types';

interface Params {
    projectId: string;
    productId: string;
    projectProductId?: string;
    mode?: string;
    commentId?: string;
}

interface Props {
    references: ProductReferences | null;
    product: Product;
    productsCount: number;
    projectData: MFRProject;
    editable: boolean;
    commentId?: string;
    selectedProductId?: string;
    openingMode?: string;
    showHelp: boolean;
    showProductItemHeader: boolean;
    teamMembers: TeamMember[];
    projectRoles: Array<SelectData>;
    docAndImagesUploads: Array<GetUploadedDocumentsAndImagesCountResponse>;
}

const MFRPProductItem: React.FC<Props> = ({
    references,
    product,
    productsCount,
    editable,
    projectData,
    commentId,
    selectedProductId,
    openingMode,
    showHelp,
    showProductItemHeader,
    teamMembers,
    projectRoles,
    docAndImagesUploads,
}) => {

    const [sustainabilityDocuments, setSustainabilityDocuments] = useState<SustainabilityDocument[]>([]);
    const [leedDocumentTypes, setLeedDocumentTypes] = useState<LeedDocumentType[]>([]);
    const [leedLEMDocumentTypes, setLeedLEMDocumentTypes] = useState<LeedDocumentType[]>([]);
    const dispatch = useAppDispatch();

    // Get cached LEED document types from Redux
    const cachedLeedDocumentTypes = useAppSelector((state) => state.productReducer.leedDocumentTypes);
    const cachedLeedLemDocumentTypes = useAppSelector((state) => state.productReducer.leedLemDocumentTypes);

    useEffect(() => {
        fetchDocuments();
    }, [cachedLeedDocumentTypes]);

    useEffect(() => {
        fetchLemDocuments();
    }, [cachedLeedLemDocumentTypes]);



    const params = useParams<Params>();

    const allowBeOpened = (params === null || params.projectProductId === null || Number(params.projectProductId) === Number(product.id));

    const fetchLemDocuments = async () => {
        // Use cached data if available, otherwise fetch
        if (cachedLeedLemDocumentTypes) {
            setLeedLEMDocumentTypes(cachedLeedLemDocumentTypes);
        } else {
            const leedDocs = await dispatch(getLeedLEMDocumentTypesAction() as any) as ActionResponse<LeedDocumentType[]>;
            setLeedLEMDocumentTypes(leedDocs.payload.data || []);
        }
    }

    const fetchDocuments = async () => {
        const docs = await getAllSustainabilityDocuments(dispatch);
        setSustainabilityDocuments(docs || []);
        
        // Use cached data if available, otherwise fetch
        if (cachedLeedDocumentTypes) {
            setLeedDocumentTypes(cachedLeedDocumentTypes);
        } else {
            const leedDocs = await dispatch(getLeedDocumentTypesAction() as any) as ActionResponse<LeedDocumentType[]>;
            setLeedDocumentTypes(leedDocs.payload.data || []);
        }
    };

    return (
        <MFRPProductItemContainer id={product.id.toString()}>
            <ProductItemHeader
                product={product}
                productsCount={productsCount}
                showCheckbox={false}
                projectInfo={projectData}
                showHelp={showHelp}
                showHeader={showProductItemHeader}
            />
            <MFRProductInfo references={references} project={projectData} product={product} editable={editable} 
                projectRoles={projectRoles}
            />
            <ProductComments
                product={product}
                projectData={projectData}
                openByDefault={openingMode === 'comments' && allowBeOpened ? 1 : 0}
                commentId={commentId}
                selectedProductId={selectedProductId}
            />
            <ProductPricing
                product={product}
                userRole={projectData.userRole}
                openPricing={commentId}
            />
            <ProductUploads
                teamMembers={teamMembers}
                docAndImagesUploads={docAndImagesUploads}
                newSustainabilityDocuments={sustainabilityDocuments}
                leedDocumentTypes={leedDocumentTypes}
                allDocumentTypes={references?.allDocumentTypes || []}
                product={product}
                project={projectData}
                openByDefault={openingMode === 'documents' && allowBeOpened ? 1 : 0}
                commentId={commentId}
                uploadType={UploadType.DOCUMENT}
            />
            {
                isFunctionalityEnabled(UPLOAD_PHOTOS) &&
                <ProductUploads
                    teamMembers={teamMembers}
                    docAndImagesUploads={docAndImagesUploads}
                    newSustainabilityDocuments={sustainabilityDocuments}
                    leedDocumentTypes={leedDocumentTypes}
                    allDocumentTypes={references?.allDocumentTypes || []}
                    product={product}
                    project={projectData}
                    openByDefault={openingMode === 'images' && allowBeOpened ? 1 : 0}
                    commentId={commentId}
                    uploadType={UploadType.PICTURE}
                />
            }
        </MFRPProductItemContainer>
    );
};

export default MFRPProductItem;
